<template>
    <div class="main-content-narrow">
        <h2 class="content-heading">
            <i class="fa fa-angle-right text-muted mr-1"/> Account Overview
            <router-link class="btn btn-hero-sm btn-hero-success float-right mr-2"
                         v-if="accountLoaded && account.account_type === 'credit'"
                         :to="{name: 'account.payments.create', params: {account: accountId}}">
                <i class="fa fa-plus"/>
                Add Funds
            </router-link>
        </h2>
        <div class="block block-rounded js-appear-enabled animated fadeIn" data-toggle="appear">
            <div class="block-content block-content-full">
                <div class="row" v-loading="pageLoading">
                    <div class="col-md-6">
                        <div class="font-size-h1 font-w300 text-black mb-1" v-if="accountLoaded">
                            {{formatAmount(account)}}
                        </div>
                        <a class="link-fx font-size-sm font-w700 text-uppercase text-muted" href="javascript:void(0)">Available
                            Balance</a>
                    </div>
                    <div class="col-md-6 text-right">
                        <div class="font-size-h1 font-w300 text-danger mb-1" v-if="accountLoaded">
                            -{{account.dailyUsage}}
                        </div>
                        <a class="link-fx font-size-sm font-w700 text-uppercase text-muted" href="javascript:void(0)">Used
                            Today</a>
                    </div>
                </div>
            </div>
        </div>
        <h2 class="content-heading mt-0 pt-0">
            <i class="fa fa-angle-right text-muted mr-1"></i> Projects ({{accountLoaded ? account.projects.data.length :
            0 }})
        </h2>
        <div v-loading="pageLoading">
            <div class="row" v-if="accountLoaded === true">
                <div v-for="(project, projectKey) in account.projects.data"
                     class="col-lg-6 js-appear-enabled animated fadeIn" data-toggle="appear" :key="projectKey">
                    <a class="block block-rounded block-bordered" href="javascript:void(0)">
                        <div class="block-content block-content-full d-flex align-items-center justify-content-between">
                            <div>
                                <p class="font-size-lg font-w600 mb-0">
                                    {{project.name}}
                                </p>
                                <p class="text-muted mb-0 small">
                                    {{project.description}}
                                </p>
                            </div>
                            <div class="ml-3">
                                <i class="fa fa-briefcase fa-2x text-gray"></i>
                            </div>
                        </div>
                        <div class="block-content block-content-full block-content-sm text-center bg-body-light">
                            <em class="font-size-sm text-muted">
                                <strong>{{account.currency}} {{projectUsage(project.id)}}</strong> monthly usage
                            </em>
                        </div>
                    </a>
                </div>
            </div>
        </div>
        <h2 class="content-heading">
            <i class="fa fa-angle-right text-muted mr-1"/> Latest Transactions
        </h2>
        <div v-loading="pageLoading">
            <div v-if="accountLoaded === true">
                <a v-for="(transaction, trnx) in account.payments.data" :key="trnx"
                   :class="{'border-success': transaction.isSuccessful, 'border-danger': !transaction.isSuccessful}"
                   class="block block-rounded block-link-shadow  border-left border-success border-3x"
                   data-toggle="appear"
                   href="javascript:void(0)">
                    <div class="block-content block-content-full d-flex align-items-center justify-content-between">
                        <div>
                            <p class="font-size-lg font-w600 mb-0">
                                {{formatAmount({balance: transaction.amount, currency: transaction.currency})}}
                            </p>
                            <p class="text-muted mb-0">
                                {{transaction.gateway}}
                            </p>
                        </div>
                        <div class="ml-3">
                            <i :class="{'fa-arrow-left text-success': incomingPayment(transaction),  'fa-arrow-right text-danger': outgoingPayment(transaction)}"
                               class="fa fa-2x"/>
                        </div>
                    </div>
                    <div v-if="incomingPayment(transaction)" class="block-content block-content-full block-content-sm bg-body-light">
                        <span class="font-size-sm text-muted">Received From <strong>{{transaction.from}}</strong> at <strong>{{transaction.created_at}}</strong></span>
                    </div>
                    <div v-if="outgoingPayment(transaction)" class="block-content block-content-full block-content-sm bg-body-light">
                        <span class="font-size-sm text-muted">Sent To <strong>{{transaction.recipient}}</strong> at <strong>{{transaction.created_at}}</strong></span>
                    </div>
                </a>
                <div class="text-center js-appear-enabled animated fadeIn" data-toggle="appear">
                    <a class="btn btn-sm btn-secondary font-w600" href="javascript:void(0)">
                        <i class="fa fa-arrow-down mr-1"/> View More..
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import numeral from 'numeral';

    export default {
        name: "AccountView",
        data() {
            return {
                account: null,
                accountId: null,
                pageLoading: false,
                projectAccountUsage: [],
                accountLoaded: false
            }
        },
        created() {
            this.accountId = this.$route.params.account;
        },
        mounted() {
            this.getAccountDetails();
        },
        methods: {
            getAccountDetails() {
                this.pageLoading = true;
                axios.get(`/billing/accounts/${this.accountId}`, {
                    params: {
                        include: 'projects,payments',
                        paymentsLimit: 5,
                        includeAccountUsage: 1,
                    }
                }).then(res => {
                    this.account = res.data.data;
                    this.pageLoading = false;
                    this.accountLoaded = true;
                    this.getProjectUsage(res.data.data.projects.data)
                })
            },

            incomingPayment(transaction) {
                return _.includes(['CREDITS_TRANSFER_RECEIVE', 'CREDITS_PURCHASE', "DEPOSIT"], transaction.transactionType)
            },

            outgoingPayment(transaction) {
                return _.includes(['CREDITS_TRANSFER_SEND', "PAYOUT"], transaction.transactionType)
            },

            getProjectUsage(projects) {
                if (projects.length > 0) {
                    let promises = projects.map(project => {
                        return axios.get(`projects/${project.id}/accounts/${this.accountId}/usage`)
                    });
                    Promise.all(promises).then(results => {
                        this.projectAccountUsage = results.map(res => res.data.data);
                    })
                }
            },

            projectUsage(project) {
                if (this.projectUsage.length > 0) {
                    let result = this.projectAccountUsage.filter(item => item.project === project)[0]
                    return typeof result === "object" ? result.usage : 0
                }
                return 0;
            },
            /**
             *
             * @param account
             * @returns {string}
             */
            formatAmount(account) {
                return `${account.currency} ` + numeral(account.balance).format('0,0.00')
            }
        }
    }
</script>

<style scoped>

</style>
